/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2023, 2024
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import { createAction, createAsyncThunk } from '@reduxjs/toolkit';
import _ from 'lodash';

import {
  selectAssistantById,
  selectDialogSkillIdFromAssistant
} from '@ibm-watson/shared/modules/ia/store/assistants/core-selectors';
import { addErrorMessage } from '@ibm-watson/shared/modules/ia/store/messages/actions';
import { listUserLabels } from '@ibm-watson/shared/modules/ia/store/skills/actions';
import { ApiStatus } from '@ibm-watson/shared/ts';
import { IntegrationSchema } from '@ibm-watson/shared/ts/integration';
import { AsyncAppThunk, CreateAsyncThunk } from '@ibm-watson/shared/ts/state';
import * as api from '../../../api/integrations/classic';
import * as integrationServerApi from '../../../api/integrations/integrationServer';
import {
  FIELDS as WEBCHAT_FIELDS,
  SUBSCRIBE_PAYLOAD_TRANSFORM as WEBCHAT_TRANSFORM,
  SUBSCRIBE_PAYLOAD_TRANSFORM_ONLY_GENERATED_KEY as WEBCHAT_TRANSFORM_ONLY_GENERATED_KEY
} from '../../../components/web-chat/schema';
import { SUBSCRIBE_PAYLOAD_TRANSFORM as WHATSAPP_TRANSFORM } from '../../../components/whatsapp/schema';
import {
  SMS_CHANNEL_TYPE,
  WEBCHAT_CHANNEL_TYPE,
  WHATSAPP_CHANNEL_TYPE
} from '../../../util/channelTypes';
import * as messages from '../messages';
import { selectDraftIntegration, selectIntegrationById } from '../selectors';

/**
 * List integrations
 *
 * @param {string} assistantId - Assistant ID
 */
export const listIntegrations = createAsyncThunk<
  IntegrationSchema[],
  string,
  CreateAsyncThunk
>('integrations/listClassicIntegrations', async (assistantId, { dispatch }) => {
  try {
    const response = await api.listIntegrations(assistantId);
    return response.integrations;
  } catch (error) {
    dispatch(addErrorMessage(error));
    throw error;
  }
});

export const listAllAssistantsIntegrations = createAsyncThunk<
  IntegrationSchema[],
  string,
  CreateAsyncThunk
>(
  'integrations/listAllClassicIntegrations',
  async (assistantIds, { dispatch }) => {
    try {
      let integrations = [];
      for (const assistantId of assistantIds) {
        const response = await api.listIntegrations(assistantId);
        integrations = integrations.concat(response.integrations);
      }

      return integrations;
    } catch (error) {
      dispatch(addErrorMessage(error));
      throw error;
    }
  }
);

type LoadCurrentIntegration = {
  assistantId: string;
  integrationId: string;
  mergeDraft?: boolean;
};
/**
 *
 *
 */
export const loadCurrentIntegration = createAsyncThunk<
  IntegrationSchema,
  LoadCurrentIntegration,
  CreateAsyncThunk
>(
  'integrations/loadClassicIntegration',
  async (
    { assistantId, integrationId, mergeDraft },
    { dispatch, getState }
  ) => {
    try {
      const integration = await api.getIntegration(assistantId, integrationId);

      if (mergeDraft === true) {
        const draftIntegration = selectDraftIntegration(getState());
        if (!_.isNil(draftIntegration?.settings)) {
          integration.settings = {
            ...draftIntegration.settings,
            ...integration.settings
          };
        }
      }
      return integration;
    } catch (error) {
      dispatch(addErrorMessage(error));
      throw error;
    }
  }
);

type CreateIntegration = {
  assistantId: string;
  integration: any;
  markAsPremature?: boolean;
};
/**
 *
 *
 */
export const createIntegration = createAsyncThunk<
  IntegrationSchema,
  CreateIntegration,
  CreateAsyncThunk
>(
  'integrations/createClassicIntegration',
  async ({ assistantId, integration }, { dispatch }) => {
    try {
      return api.createIntegration(assistantId, integration);
    } catch (error) {
      dispatch(addErrorMessage(error));
      throw error;
    }
  }
);

/**
 *
 *
 */
export const selectClassicIntegration = createAction(
  'integration/selectClassicIntegration',
  function prepare(show_overlay, integration_id, integration, channel_type) {
    return {
      payload: {
        show_overlay,
        integration,
        integration_id,
        channel_type
      }
    };
  }
);

/**
 * Safe variant of creating integrations.
 *
 * @param {string} assistantId - Assistant ID
 * @param {IntegrationSchema} integration - Integration object
 * @param {boolean} selectCurrentIntegration - If false, do not update
 */
export const createNewIntegration = (
  assistantId: string,
  integration: Partial<IntegrationSchema>,
  selectCurrentIntegration: boolean = true
): AsyncAppThunk<Promise<IntegrationSchema>> => {
  return async (dispatch, getState) => {
    const { status } = getState()?.integrations?.createIntegration;

    // This mitigates a backend bug, where if private.secrets is
    // undefined, then future subscribe calls would fail
    // (e.g. cannot set properties of undefined).
    if (
      integration.type === SMS_CHANNEL_TYPE &&
      _.isNil(integration?.settings?.private?.secrets)
    ) {
      _.set(integration, 'settings.private.secrets.twilio_account_sid', '');
    }

    const responseIntegration: IntegrationSchema = await dispatch(
      createIntegration({ assistantId, integration })
    ).unwrap();

    if (selectCurrentIntegration && status != ApiStatus.ERROR) {
      dispatch(
        selectClassicIntegration(
          true,
          responseIntegration.integration_id,
          responseIntegration,
          responseIntegration.type
        )
      );
    }

    return responseIntegration;
  };
};

type UpdateIntegration = {
  assistantId: string;
  integrationId?: string;
  integration: Partial<IntegrationSchema>;
};
/**
 *
 *
 */
export const updateIntegration = createAsyncThunk<
  IntegrationSchema,
  UpdateIntegration,
  CreateAsyncThunk
>(
  'integrations/updateClassicIntegration',
  async ({ assistantId, integrationId, integration }, { dispatch }) => {
    try {
      const safeIntegrationId = integrationId ?? integration.integration_id;
      return api.updateIntegration(assistantId, safeIntegrationId, integration);
    } catch (error) {
      dispatch(addErrorMessage(error));
      throw error;
    }
  }
);

type DeleteIntegration = {
  assistantId: string;
  integrationId: string;
};
type DeleteIntegrationPayload = {
  integrationId: string;
  serviceDeskId?: string;
};
/**
 *
 *
 */
export const deleteIntegration = createAsyncThunk<
  DeleteIntegrationPayload,
  DeleteIntegration,
  CreateAsyncThunk
>(
  'integrations/deleteClassicIntegration',
  async ({ assistantId, integrationId }, { dispatch, getState }) => {
    try {
      const retObj = { integrationId, serviceDeskId: null };
      const integration = selectIntegrationById(getState(), integrationId);

      // Unsubscribe the integration
      // If this fails, that's okay and we continue
      try {
        await integrationServerApi.unsubscribe(
          integration.type,
          integration.integration_id
        );
      } catch (error) {
        console.error(error);
      }

      // If channel is webchat, unsubscribe and delete service desk
      if (
        integration.type === WEBCHAT_CHANNEL_TYPE &&
        _.get(integration.settings, WEBCHAT_FIELDS.SERVICE_DESK.IS_ON) ===
          true &&
        !_.isNil(
          _.get(
            integration.settings,
            WEBCHAT_FIELDS.SERVICE_DESK.INTEGRATION_TYPE
          )
        ) &&
        !_.isNil(
          _.get(
            integration.settings,
            WEBCHAT_FIELDS.SERVICE_DESK.INTEGRATION_ID
          )
        )
      ) {
        retObj.serviceDeskId = _.get(
          integration.settings,
          WEBCHAT_FIELDS.SERVICE_DESK.INTEGRATION_ID
        );

        // Unsubscribe the service desk
        // If this fails, that's okay and we continue
        try {
          await integrationServerApi.unsubscribe(
            _.get(
              integration.settings,
              WEBCHAT_FIELDS.SERVICE_DESK.INTEGRATION_TYPE
            ),
            retObj.serviceDeskId
          );
        } catch (error) {
          console.error(error);
        }

        // Unsubscribe the service desk
        // If this fails, that's okay and we continue
        try {
          await api.deleteIntegration(assistantId, retObj.serviceDeskId);
        } catch (error) {
          console.error(error);
        }
      }

      // Delete the integration
      await api.deleteIntegration(assistantId, integrationId);

      return retObj;
    } catch (error) {
      dispatch(addErrorMessage(error));
      // deletedObjectEvents
      throw error;
    }
  }
);

interface DescribeIntegrationProps {
  assistantId: string;
  integrationId?: string;
  integrationType?: string;
  integration: any;
}
/**
 *
 *
 */
export const describeIntegration = createAsyncThunk<
  any,
  DescribeIntegrationProps,
  CreateAsyncThunk
>(
  'integrations/describeClassicIntegration',
  async (
    { integrationId, integrationType, integration },
    { dispatch, getState }
  ) => {
    try {
      const safeIntegrationId = integrationId ?? integration.integration_id;
      const safeIntegrationType = integrationType ?? integration.type;
      const assistantId = integration.assistant_id;

      const response = await integrationServerApi.getServiceDesk(
        safeIntegrationType,
        safeIntegrationId
      );

      const skillId = selectDialogSkillIdFromAssistant(getState(), assistantId);
      await dispatch(listUserLabels(skillId));

      return response;
    } catch (error) {
      if (
        getState().integrations.serviceDesk.connectServiceDesk.status ===
        ApiStatus.LOADING
      ) {
        return;
      }
      dispatch(
        addErrorMessage({
          message: messages.describeServiceDesk.failed,
          status: 'error',
          transactionId: error.transactionId
        })
      );
      throw error;
    }
  }
);

interface SubscribeIntegrationProps {
  channelType?: string;
  integrationId?: string;
  integration?: any;
  data: any;
  isGenerateWebchatKey?: boolean;
}
/**
 *
 *
 */
export const subscribeIntegration = createAsyncThunk<
  any,
  SubscribeIntegrationProps,
  CreateAsyncThunk
>(
  'integrations/subscribeClassicIntegration',
  async (
    {
      channelType,
      integrationId,
      integration,
      data = {},
      isGenerateWebchatKey = false
    },
    { dispatch, getState }
  ) => {
    try {
      const safeChannelType = channelType ?? integration.type;
      const safeIntegrationId = integrationId ?? integration.integration_id;

      const response = await integrationServerApi.subscribe(
        safeChannelType,
        safeIntegrationId,
        data,
        isGenerateWebchatKey
      );

      // Response payload transformation is needed when integrations
      // backend sometimes returns integrations values whose keys do
      // not match the keys defined by the integration schema. Thus,
      // the channels need a transform object, mapping the incorrect
      // key field to the correct one. We take that mapping, map the
      // values with their correct fields, and then merge the fields
      // with response.
      // Note that payload transformation is NOT necessary if either
      // of the following conditions are met:
      // - Integrations backend has not introduced any new fields or
      //   modified any existing fields. Here, the draftIntegration
      //   doesn't need to be updated with any changed fields.
      // - Integrations backend has mutated some fields, but the
      //   keys for all these fields are correct. In this case,
      //   updating draftIntegration would work correctly as normal.
      const transformPayloadChannels = [
        WEBCHAT_CHANNEL_TYPE,
        WHATSAPP_CHANNEL_TYPE
      ];

      if (transformPayloadChannels.includes(safeChannelType)) {
        let transformObject;
        switch (safeChannelType) {
          case WHATSAPP_CHANNEL_TYPE:
            transformObject = WHATSAPP_TRANSFORM;
            break;
          // Here we remove all fetched fields except for private.authentication.* so we don't override edits.
          case WEBCHAT_CHANNEL_TYPE:
            if (isGenerateWebchatKey) {
              transformObject = WEBCHAT_TRANSFORM_ONLY_GENERATED_KEY;
            } else {
              transformObject = WEBCHAT_TRANSFORM;
            }
            break;
          default:
            break;
        }

        return Object.keys(transformObject).reduce((resp, key) => {
          const payloadField = transformObject[key];
          _.set(resp, key, _.get(response, payloadField));
          return resp;
        }, {});
      }

      return response;
    } catch (error) {
      // Intercom error message is more specific,
      // and it will have the key `other_integration_id`
      if (!_.isEmpty(_.get(error, 'response.data.other_integration_id'))) {
        const otherIntegration = selectIntegrationById(
          getState(),
          _.get(error, 'response.data.other_integration_id')
        );
        const otherAssistant = selectAssistantById(
          getState(),
          _.get(otherIntegration, 'assistant_id')
        );

        // This is rare in V2 integrations, but in the case where
        // the integration with Intercom already connected is found
        // in the Redux state, we want to tell the user the exact
        // integration that is connected to the Intercom account.
        // Otherwise, show a generic message about the Intercom
        // account already being used by another assistant.
        if (otherIntegration && otherAssistant) {
          const message = messages.connectServiceDesk.subscriptionExists;
          const values = {
            assistant: otherAssistant.name,
            integration: otherIntegration.name
          };
          dispatch(
            addErrorMessage({
              message,
              values,
              status: 'error',
              transactionId: error.transactionId
            })
          );
        } else {
          dispatch(
            addErrorMessage({
              message: messages.connectServiceDesk.anotherSubscription,
              status: 'error',
              transactionId: error.transactionId
            })
          );
        }
      } else {
        dispatch(addErrorMessage(error));
      }

      throw error;
    }
  }
);

interface UnsubscribeIntegrationProps {
  channelType?: string;
  integrationId?: string;
  integration?: any;
}
/**
 *
 *
 */
export const unsubscribeIntegration = createAsyncThunk<
  any,
  UnsubscribeIntegrationProps,
  CreateAsyncThunk
>(
  'integrations/unsubscribeClassicIntegration',
  async ({ channelType, integrationId, integration }, { dispatch }) => {
    try {
      const safeChannelType = channelType ?? integration.type;
      const safeIntegrationId = integrationId ?? integration.integration_id;

      return integrationServerApi.unsubscribe(
        safeChannelType,
        safeIntegrationId
      );
    } catch (error) {
      dispatch(
        addErrorMessage({
          message: messages.disconnectServiceDesk.failed,
          status: 'error',
          transactionId: error.transactionId
        })
      );
    }
  }
);

/**
 *
 *
 */
export const setShowClassicIntegration = (
  show: boolean,
  integrationId?: string,
  channelType?: string
): AsyncAppThunk<any> => {
  return async (dispatch, getState) => {
    const state = getState();
    dispatch(
      selectClassicIntegration(
        show,
        integrationId,
        integrationId ? selectIntegrationById(state, integrationId) : null,
        channelType
      )
    );
  };
};

/**
 *
 *
 */
export const closeClassicIntegration = createAction(
  'integration/closeClassicIntegration'
);

/**
 * If called with true, then the integration useChannelForm knows
 * that the form watch is out of date due to a draft integration
 * update. Hence, a reset draft integration form is needed.
 */
export const resetDraftIntegrationForm = createAction(
  'integrations/resetDraftIntegrationForm',
  enabled => ({
    payload: enabled
  })
);
